<template>
  <div class="dashboard-page">
    <section class="dashboard-hero">
      <div>
        <p class="dashboard-hero__eyebrow">GamePlanet Admin</p>
        <h2>运营概览</h2>
        <p class="dashboard-hero__copy">游戏、用户、兑换码与订单数据集中看板。</p>
      </div>
      <div class="dashboard-hero__score">
        <t-progress
          theme="circle"
          :percentage="creditScore"
          :stroke-width="8"
          :color="creditColor"
          :label="false"
        />
        <div>
          <span>信用分</span>
          <strong :style="{ color: creditColor }">{{ creditScore }}</strong>
          <small>{{ creditLevel }}</small>
        </div>
      </div>
    </section>

    <section class="dashboard-grid">
      <t-card
        v-for="item in statCards"
        :key="item.key"
        class="metric-card"
        :bordered="false"
      >
        <div class="metric-card__label">{{ item.label }}</div>
        <div class="metric-card__value">{{ item.value }}</div>
      </t-card>
    </section>

    <section class="dashboard-panels">
      <t-card class="dashboard-panel" :bordered="false">
        <template #title>信用变更记录</template>
        <t-table
          v-if="scoreRecords.length"
          row-key="datetime"
          :data="scoreRecords"
          :columns="scoreColumns"
          size="medium"
        />
        <t-empty v-else description="暂无信用变更记录" />
      </t-card>

      <t-card class="dashboard-panel dashboard-panel--wide" :bordered="false">
        <template #title>公告信息</template>
        <t-table
          v-if="announcements.length"
          row-key="pushtime"
          :data="announcements"
          :columns="announcementColumns"
          size="medium"
        />
        <t-empty v-else description="暂无公告" />
      </t-card>
    </section>
  </div>
</template>

<script>
import { computed, h, onMounted, ref } from 'vue';
import { MessagePlugin, Tag } from 'tdesign-vue-next';
import axios from '../axios';

export default {
  name: 'HomePage',
  setup() {
    const userInfo = ref({});
    const stats = ref({});
    const announcements = ref([]);
    const creditScore = ref(0);
    const scoreRecords = ref([]);

    const statLabels = {
      totalCreateCards: '累计制卡次数',
      totalUsedCodes: '累计兑换码使用人次',
      todayCreateCards: '当天制卡次数',
      todayUsedCodes: '当天兑换码使用人次',
      todayRegisterUsers: '当天注册用户数量',
      totalRegisterUsers: '累计注册用户数量',
      totalBannedUsers: '累计封禁用户数量',
      totalPublishedGames: '累计发布游戏数量',
      totalOrders: '累计订单数量',
      todayOrdersPeople: '当天下单人次',
      todayOrdersOK: '当天付款人次',
      todayOrdersPrice: '已付订单总额',
      totalBannedCodes: '累计封卡次数',
    };

    const statCards = computed(() =>
      Object.entries(stats.value || {}).map(([key, value]) => ({
        key,
        label: statLabels[key] || key,
        value,
      }))
    );

    const creditLevel = computed(() => {
      if (creditScore.value >= 85) return '信用优秀';
      if (creditScore.value >= 70) return '信用良好';
      if (creditScore.value >= 50) return '需要关注';
      return '风险较高';
    });

    const creditColor = computed(() => {
      if (creditScore.value >= 85) return '#00a870';
      if (creditScore.value >= 70) return '#d98500';
      if (creditScore.value >= 50) return '#e34d59';
      return '#6b7280';
    });

    const compareTime = (a, b, key) => new Date(a[key]).getTime() - new Date(b[key]).getTime();

    const scoreColumns = [
      { colKey: 'datetime', title: '变更时间', width: 180, sorter: (a, b) => compareTime(a, b, 'datetime') },
      { colKey: 'processor', title: '处理人', width: 120 },
      {
        colKey: 'score',
        title: '变更分数',
        width: 120,
        cell: (hRenderer, { row }) =>
          h(
            'span',
            { class: row.types > 0 ? 'score-up' : 'score-down' },
            `${row.types > 0 ? '+' : '-'}${row.score}`
          ),
      },
      { colKey: 'reason', title: '变更原因' },
    ];

    const announcementColumns = [
      { colKey: 'username', title: '发布人', width: 140 },
      { colKey: 'pushtime', title: '发布时间', width: 190, sorter: (a, b) => compareTime(a, b, 'pushtime') },
      {
        colKey: 'info',
        title: '内容',
        cell: (hRenderer, { row }) => h(Tag, { theme: 'default', variant: 'light' }, () => row.info),
      },
    ];

    const tokenPayload = () => ({ token: localStorage.getItem('user-token') });

    const fetchUserInfo = async () => {
      try {
        const response = await axios.post('/index.php?action=getUserInfos', tokenPayload());
        if (response.data.code === 200) {
          userInfo.value = response.data.user;
          creditScore.value = Number(response.data.user.score || 0);
        } else {
          MessagePlugin.error(response.data.message || '获取用户信息失败');
        }
      } catch (error) {
        MessagePlugin.error('获取用户信息失败');
      }
    };

    const fetchStats = async () => {
      try {
        const response = await axios.post('/index.php?action=getStats', tokenPayload());
        if (response.data.code === 200) {
          stats.value = response.data.stats || {};
        } else {
          MessagePlugin.error(response.data.message || '获取统计数据失败');
        }
      } catch (error) {
        MessagePlugin.error('获取统计数据失败');
      }
    };

    const fetchAnnouncements = async () => {
      try {
        const response = await axios.post('/index.php?action=getAnnouncements', tokenPayload());
        announcements.value = response.data.success ? response.data.announcements || [] : [];
      } catch (error) {
        announcements.value = [];
      }
    };

    const fetchScoreRecords = async () => {
      try {
        const response = await axios.post('/index.php?action=getScoreRecords', {
          ...tokenPayload(),
          limit: 3,
        });
        scoreRecords.value = response.data.success ? response.data.records || [] : [];
      } catch (error) {
        scoreRecords.value = [];
      }
    };

    onMounted(() => {
      fetchUserInfo();
      fetchStats();
      fetchAnnouncements();
      fetchScoreRecords();
    });

    return {
      announcementColumns,
      announcements,
      creditColor,
      creditLevel,
      creditScore,
      scoreColumns,
      scoreRecords,
      statCards,
      userInfo,
    };
  },
};
</script>

<style scoped>
.dashboard-page {
  display: grid;
  gap: 18px;
}

.dashboard-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
}

.dashboard-hero__eyebrow {
  margin: 0 0 8px;
  color: #0052d9;
  font-size: 12px;
  font-weight: 700;
}

.dashboard-hero h2 {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
}

.dashboard-hero__copy {
  margin: 10px 0 0;
  color: #667085;
}

.dashboard-hero__score {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 250px;
  padding: 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
}

.dashboard-hero__score div {
  display: grid;
  gap: 4px;
}

.dashboard-hero__score span,
.dashboard-hero__score small,
.metric-card__label {
  color: #667085;
  font-size: 12px;
}

.dashboard-hero__score strong {
  font-size: 30px;
  line-height: 1;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
}

.metric-card {
  border: 1px solid #e7edf5;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.metric-card__label {
  margin-bottom: 10px;
}

.metric-card__value {
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.dashboard-panels {
  display: grid;
  grid-template-columns: minmax(320px, 0.9fr) minmax(420px, 1.1fr);
  gap: 16px;
}

.dashboard-panel {
  border: 1px solid #e7edf5;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.score-up {
  color: #00a870;
  font-weight: 700;
}

.score-down {
  color: #e34d59;
  font-weight: 700;
}

@media (max-width: 900px) {
  .dashboard-hero,
  .dashboard-hero__score {
    align-items: flex-start;
    flex-direction: column;
  }

  .dashboard-hero__score {
    width: 100%;
  }

  .dashboard-panels {
    grid-template-columns: 1fr;
  }
}
</style>
